import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { User, CreatorProfile } from '@/types';
import { supabase } from '../supabase/client';
import { authMonitor } from './monitoring';
import { traceAuthFlow } from './debug';
import { authRateLimiter } from './rate-limiter';
import { tokenManager } from './token-manager';
import { logger } from '../utils/logger';
import { mapMemberRole, type MemberAppRole } from './member-role';

export { mapMemberRole } from './member-role';

type MemberRow = {
  id: string;
  email?: string | null;
  full_name?: string | null;
  is_admin?: boolean | null;
  role?: string | null;
  phone_number?: string | null;
  instagram_handle?: string | null;
  approval_status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

function normalizeMembershipStatus(approvalStatus?: string | null): 'active' | 'pending' | undefined {
  if (approvalStatus === 'approved') return 'active';
  if (approvalStatus === 'pending') return 'pending';
  return undefined;
}

type Permission = string;
type Role = MemberAppRole;

/**
 * 
 * Eliminates circular dependencies by combining auth and profile concerns.
 * 
 */
export class UnifiedAuthService {
  private session: Session | null = null;
  private loading = true;
  private initialized = false;
  
  private userProfile: User | null = null;
  private creatorProfile: CreatorProfile | null = null;
  private profileCacheTimestamp = 0;
  private readonly profileCacheTTL = 5 * 60 * 1000; // 5 minutes
  
  private authListeners = new Set<(session: Session | null) => void>();
  private profileListeners = new Set<(user: User | null) => void>();
  // Listeners receive `Error` for a new error and `null` when the error is
  // cleared (e.g. on sign-out or a successful profile fetch), so that already-
  // mounted subscribers can clear their local error state.
  private errorListeners = new Set<(error: Error | null) => void>();

  // Last emitted error — replayed to late onError subscribers, mirroring the
  // onAuthStateChange / onProfileChange replay of current state once
  // `initialized` is true. Cleared on profile-success and sign-out.
  private lastError: Error | null = null;
  
  constructor() {
    this.initialize();
  }
  
  private async initialize() {
    try {
      if (!authRateLimiter.canMakeRequest('sessionRestore')) {
        logger.warn('Rate limit hit during session restore');
        this.setInitialized();
        return;
      }
      
      authMonitor.startTimer('sessionRestore');
      traceAuthFlow('UnifiedAuthService.initialize');
      
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        authMonitor.endTimer('sessionRestore', false, error);
        logger.error('Failed to restore session', { error });
        
        if (this.isInvalidTokenError(error)) {
          await this.clearInvalidSession();
        } else {
          this.notifyError(error);
        }
        
        this.setInitialized();
        return;
      }
      
      if (session) {
        this.session = session;
        // Await profile so role-gated routes never see user + null profile (silent bounce).
        await this.fetchProfileInBackground(session.user.id);
      }
      
      authMonitor.endTimer('sessionRestore', true);
      this.setInitialized();
      
      supabase.auth.onAuthStateChange(this.handleAuthStateChange.bind(this));
      
    } catch (error) {
      authMonitor.endTimer('sessionRestore', false, error as Error);
      logger.error('Failed to initialize auth', { error });
      this.notifyError(error as Error);
      this.setInitialized();
    }
  }
  
  private setInitialized() {
    this.loading = false;
    this.initialized = true;
    this.notifyAuthListeners(this.session);
    this.notifyProfileListeners(this.userProfile);
  }
  
  private async handleAuthStateChange(event: string, session: Session | null) {
    traceAuthFlow(`AuthStateChange: ${event}`, { userId: session?.user?.id });
    
    switch (event) {
      case 'SIGNED_IN':
        this.session = session;
        logger.info('User signed in', { email: session?.user?.email });
        if (session) {
          await this.fetchProfileInBackground(session.user.id);
        }
        break;
        
      case 'SIGNED_OUT':
        this.clearState();
        logger.info('User signed out');
        break;
        
      case 'TOKEN_REFRESHED':
        if (!session) {
          logger.warn('Token refresh failed');
          await this.signOut();
        } else {
          this.session = session;
        }
        break;
        
      case 'USER_UPDATED':
        this.session = session;
        logger.info('User updated', { email: session?.user?.email });
        if (session) {
          await this.fetchProfileInBackground(session.user.id, true);
        }
        break;
    }
    
    this.notifyAuthListeners(session);
  }
  
  private async fetchProfileInBackground(userId: string, forceRefresh = false) {
    try {
      if (!forceRefresh && this.isProfileCacheValid()) {
        return;
      }
      
      authMonitor.startTimer('profileFetch');
      
      // members is the only profile source (public.users was dropped).
      const fetchMemberPromise = supabase
        .from('members')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // Add timeout for mobile responsiveness
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 3000)
      );

      const { data: member, error: memberError } = await Promise.race([
        fetchMemberPromise,
        timeoutPromise
      ]) as { data: MemberRow | null; error: { message?: string } | null };

      if (memberError || !member) {
        const err = memberError instanceof Error
          ? memberError
          : new Error(memberError?.message || 'Member profile not found');
        logger.error('Failed to fetch member profile', { error: err, userId });
        this.userProfile = null;
        this.creatorProfile = null;
        this.notifyProfileListeners(null);
        this.notifyError(err);
        authMonitor.endTimer('profileFetch', false, err);
        return;
      }

      const role = mapMemberRole(member);
      const user = {
        id: member.id,
        email: member.email || '',
        full_name: member.full_name || 'Member',
        role,
        phone_number: member.phone_number ?? undefined,
        instagram: member.instagram_handle ?? undefined,
        membership_status: normalizeMembershipStatus(member.approval_status),
        created_at: member.created_at ?? '',
        updated_at: member.updated_at ?? '',
        status: (member.approval_status === 'approved' || role === 'admin'
          ? 'active'
          : member.approval_status === 'pending'
            ? 'pending'
            : 'active') as User['status'],
      };
      
      this.userProfile = user;
      this.profileCacheTimestamp = Date.now();
      
      // Clear any stale profile-load error: tell late-subscriber replay and
      // already-mounted subscribers that there is no current error.
      this.lastError = null;
      this.notifyErrorListeners(null);
      
      this.notifyProfileListeners(this.userProfile);
      
      // Fetch creator profile in parallel if user is a creator (don't block UI)
      if (role === 'creator' || role === 'admin') {
        // Use explicit field selection to avoid 406 errors on mobile
        const fetchCreatorProfile = async () => {
          try {
            const { data: creator, error } = await supabase
              .from('creator_profiles')
              .select('*')
              .eq('user_id', userId)
              .maybeSingle();
            
            if (error) {
              logger.warn('Creator profile fetch failed', { error });
              this.creatorProfile = null;
            } else {
              this.creatorProfile = creator;
            }
            this.notifyProfileListeners(this.userProfile);
          } catch (error) {
            logger.warn('Creator profile fetch error', { error });
            this.creatorProfile = null;
          }
        };
        
        fetchCreatorProfile();
      } else {
        this.creatorProfile = null;
      }
      
      authMonitor.endTimer('profileFetch', true);
      
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Error fetching profile');
      logger.error('Error fetching profile', { error: err });
      this.userProfile = null;
      this.creatorProfile = null;
      this.notifyProfileListeners(null);
      this.notifyError(err);
      authMonitor.endTimer('profileFetch', false, err);
    }
  }
  
  private isProfileCacheValid(): boolean {
    return (
      this.userProfile !== null &&
      Date.now() - this.profileCacheTimestamp < this.profileCacheTTL
    );
  }
  
  private isInvalidTokenError(error: any): boolean {
    return (
      error?.message?.includes('refresh_token_not_found') ||
      error?.message?.includes('invalid refresh token') ||
      error?.message?.includes('Token has expired') ||
      error?.status === 400 ||
      error?.status === 401
    );
  }
  
  private async clearInvalidSession() {
    logger.warn('Clearing invalid session');
    await supabase.auth.signOut();
    this.clearState();
  }
  
  private clearState() {
    this.session = null;
    this.userProfile = null;
    this.creatorProfile = null;
    this.profileCacheTimestamp = 0;
    this.lastError = null;
    // Notify listeners of the state change; null signals error-cleared to
    // already-mounted subscribers so they can clear their local error state.
    this.notifyAuthListeners(null);
    this.notifyProfileListeners(null);
    this.notifyErrorListeners(null);
    tokenManager.reset();
  }
  
  
  async signIn(email: string, password: string) {
    try {
      authMonitor.startTimer('signIn');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        authMonitor.endTimer('signIn', false, error);
        throw error;
      }
      
      authMonitor.endTimer('signIn', true);
      return data;
      
    } catch (error) {
      logger.error('Sign in failed', { error });
      throw error;
    }
  }
  
  async signInWithProvider(provider: 'google') {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) throw error;
      
    } catch (error) {
      logger.error('OAuth sign in failed', { error });
      throw error;
    }
  }
  
  async signUp(email: string, password: string, metadata?: Record<string, any>) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata }
      });
      
      if (error) throw error;
      return data;
      
    } catch (error) {
      logger.error('Sign up failed', { error });
      throw error;
    }
  }
  
  async signOut() {
    try {
      logger.info('Starting sign out process');
      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error('Supabase signOut returned error', { error });
        throw error;
      }
      this.clearState();
      logger.info('Sign out completed successfully');
    } catch (error) {
      logger.error('Sign out failed', { error });
      this.clearState();
      throw error;
    }
  }
  
  async refreshSession() {
    try {
      const session = await tokenManager.refreshToken();
      return session;
    } catch (error) {
      logger.error('Session refresh failed', { error });
      throw error;
    }
  }
  
  
  async getProfile(forceRefresh = false): Promise<User | null> {
    if (!this.session) return null;
    
    if (forceRefresh || !this.isProfileCacheValid()) {
      await this.fetchProfileInBackground(this.session.user.id, forceRefresh);
    }
    
    return this.userProfile;
  }
  
  async updateProfile(updates: Partial<User>) {
    if (!this.session) throw new Error('Not authenticated');

    try {
      const { data: memberCheck, error: memberCheckError } = await supabase
        .from('members')
        .select('id')
        .eq('id', this.session.user.id)
        .maybeSingle();

      if (memberCheckError || !memberCheck) {
        const message = memberCheckError?.message || 'Member profile not found';
        throw memberCheckError instanceof Error ? memberCheckError : new Error(message);
      }

      const memberUpdates: Record<string, unknown> = {
        updated_at: new Date().toISOString()
      };

      if (updates.email) memberUpdates.email = updates.email;
      if (updates.phone_number) memberUpdates.phone_number = updates.phone_number;
      if (updates.instagram) memberUpdates.instagram_handle = updates.instagram;
      if (updates.full_name) memberUpdates.full_name = updates.full_name;

      const { error } = await supabase
        .from('members')
        .update(memberUpdates)
        .eq('id', this.session.user.id)
        .select()
        .single();

      if (error) throw error;

      if (updates.full_name) {
        await supabase.auth.updateUser({
          data: { full_name: updates.full_name }
        });
      }

      // Remap members row → User shape (do not stash raw members row as profile)
      await this.fetchProfileInBackground(this.session.user.id, true);
      return this.userProfile;

    } catch (error) {
      logger.error('Profile update failed', { error });
      throw error;
    }
  }
  
  async updatePassword(newPassword: string) {
    if (!this.session) throw new Error('Not authenticated');
    
    try {
      authMonitor.startTimer('updatePassword');
      logger.info('Updating user password');
      
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        authMonitor.endTimer('updatePassword', false, error);
        throw error;
      }
      
      authMonitor.endTimer('updatePassword', true);
      logger.info('Password updated successfully');
      
      return data;
      
    } catch (error) {
      logger.error('Password update failed', { error });
      throw error;
    }
  }
  
  async resetPassword(email: string) {
    try {
      authMonitor.startTimer('resetPassword');
      logger.info('Sending password reset email', { email });
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });
      
      if (error) {
        authMonitor.endTimer('resetPassword', false, error);
        throw error;
      }
      
      authMonitor.endTimer('resetPassword', true);
      logger.info('Password reset email sent successfully');
      
    } catch (error) {
      logger.error('Password reset failed', { error });
      throw error;
    }
  }
  
  
  getSession(): Session | null {
    return this.session;
  }
  
  getUser(): SupabaseUser | null {
    return this.session?.user ?? null;
  }
  
  getUserProfile(): User | null {
    return this.userProfile;
  }
  
  getCreatorProfile(): CreatorProfile | null {
    return this.creatorProfile;
  }
  
  isAuthenticated(): boolean {
    return !!this.session;
  }
  
  isLoading(): boolean {
    return this.loading;
  }
  
  isInitialized(): boolean {
    return this.initialized;
  }
  
  
  hasPermission(permission: Permission): boolean {
    if (!this.userProfile) return false;
    const role = this.userProfile.role as Role;
    
    const permissions: Record<Role, Set<Permission>> = {
      user: new Set([
        'profile.view',
        'profile.edit.own',
        'experiences.view',
        'experiences.book',
        'bookings.view.own'
      ]),
      creator: new Set([
        'profile.view',
        'profile.edit.own',
        'experiences.view',
        'experiences.book',
        'experiences.create',
        'experiences.edit.own',
        'bookings.view.own',
        'bookings.view.for_experiences',
        'creator.dashboard.view'
      ]),
      admin: new Set(['*'])
    };
    
    const rolePermissions = permissions[role];
    return rolePermissions.has('*') || rolePermissions.has(permission);
  }
  
  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }
  
  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }
  
  
  onAuthStateChange(listener: (session: Session | null) => void): () => void {
    this.authListeners.add(listener);
    
    if (this.initialized) {
      listener(this.session);
    }
    
    return () => {
      this.authListeners.delete(listener);
    };
  }
  
  onProfileChange(listener: (user: User | null) => void): () => void {
    this.profileListeners.add(listener);
    
    if (this.initialized) {
      listener(this.userProfile);
    }
    
    return () => {
      this.profileListeners.delete(listener);
    };
  }
  
  onError(listener: (error: Error | null) => void): () => void {
    this.errorListeners.add(listener);
    
    if (this.initialized && this.lastError) {
      listener(this.lastError);
    }
    
    return () => {
      this.errorListeners.delete(listener);
    };
  }
  
  
  private notifyAuthListeners(session: Session | null) {
    this.authListeners.forEach(listener => {
      try {
        listener(session);
      } catch (error) {
        logger.error('Auth listener error', { error });
      }
    });
  }
  
  private notifyProfileListeners(user: User | null) {
    this.profileListeners.forEach(listener => {
      try {
        listener(user);
      } catch (error) {
        logger.error('Profile listener error', { error });
      }
    });
  }
  
  private notifyError(error: Error) {
    this.lastError = error;
    this.notifyErrorListeners(error);
  }

  private notifyErrorListeners(error: Error | null) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        logger.error('Error listener error', { error: err });
      }
    });
  }
}

export const unifiedAuthService = new UnifiedAuthService();

if (typeof window !== 'undefined') {
  (window as any).unifiedAuthService = unifiedAuthService;
}
